import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Privacy Policy',
  lastUpdated: 'January 1, 2025',
  sections: [{
    heading: 'Information We Collect',
    body: 'When you purchase our product, we collect your name, email address, and payment information (processed securely by our payment processor). We do not store your full credit card details on our servers.'
  }, {
    heading: 'How We Use Your Information',
    body: 'We use your information solely to deliver your digital product, send order confirmations, and provide customer support. We do not sell, rent, or share your personal information with third parties for marketing purposes.'
  }, {
    heading: 'Digital Product Delivery',
    body: 'Your email address is used to deliver your purchased digital product and any related updates. You may receive occasional emails related to your purchase.'
  }, {
    heading: 'Cookies',
    body: 'Our website may use cookies to improve your browsing experience. These are standard analytics cookies and do not collect personally identifiable information beyond what you voluntarily provide.'
  }, {
    heading: 'Data Security',
    body: 'We implement industry-standard security measures to protect your personal information. All transactions are encrypted using SSL technology.'
  }, {
    heading: 'Your Rights',
    body: 'You may request access to, correction of, or deletion of your personal data at any time by contacting us. We will respond to all requests within 30 days.'
  }, {
    heading: 'Changes to This Policy',
    body: 'We reserve the right to update this Privacy Policy at any time. Changes will be posted on this page with an updated revision date.'
  }]
};
const termsContent = {
  title: 'Terms & Conditions',
  lastUpdated: 'January 1, 2025',
  sections: [{
    heading: 'Digital Product — No Refund on Download',
    body: 'Upon purchase, you receive immediate access to a digital product. Due to the nature of digital goods, all sales are final once the product has been downloaded. However, if you have not downloaded the product, you may request a refund within 30 days under our Clean Slate Guarantee.'
  }, {
    heading: 'Educational Purpose Only',
    body: 'The Credit Repair Sledgehammer is an educational guide only. We are not attorneys and nothing in this product constitutes legal advice. The information is provided for educational and informational purposes only. Always consult a licensed attorney for legal advice specific to your situation.'
  }, {
    heading: 'No Guaranteed Results',
    body: 'Results vary based on individual credit history, the accuracy of information on your credit report, and the responsiveness of credit bureaus. We make no guarantee of specific outcomes, score increases, or deletion of specific items.'
  }, {
    heading: 'Intellectual Property',
    body: 'All content, templates, and materials included in this product are the intellectual property of The Credit Repair Sledgehammer. You may not reproduce, distribute, resell, or share this product or its contents without explicit written permission.'
  }, {
    heading: 'Limitation of Liability',
    body: 'To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of this product or the information contained within it.'
  }, {
    heading: 'Governing Law',
    body: 'These Terms shall be governed by and construed in accordance with the laws of the United States. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the applicable courts.'
  }, {
    heading: 'Acceptance of Terms',
    body: 'By purchasing and/or downloading this product, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions.'
  }]
};
function LegalModal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-24">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-25" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.25
    }} className="relative bg-dark-50 border border-dark-200 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-26">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-200 flex-shrink-0" data-id="element-27">
          <div data-id="element-28">
            <h2 className="text-2xl font-heading font-bold text-white" data-id="element-29">
              {content.title}
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-id="element-30">
              Last updated: {content.lastUpdated}
            </p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-dark-100 hover:bg-dark-200 flex items-center justify-center text-gray-400 hover:text-white transition-colors" data-id="element-31">
            <X className="w-5 h-5" data-id="element-32" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-6 space-y-6" data-id="element-33">
          {content.sections.map((section, idx) => <div key={idx} data-id="element-34">
              <h3 className="text-base font-bold text-gold mb-2 font-heading" data-id="element-35">
                {idx + 1}. {section.heading}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-36">
                {section.body}
              </p>
            </div>)}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-dark-200 flex-shrink-0 text-center" data-id="element-37">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-white transition-colors underline underline-offset-2" data-id="element-38">
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-10 bg-dark-DEFAULT border-t border-dark-200 text-center text-sm" data-id="element-39">
        <div className="max-w-4xl mx-auto px-4" data-id="element-40">
          <p className="text-gray-600 mb-4 text-xs leading-relaxed max-w-2xl mx-auto" data-id="element-41">
            <strong className="text-gray-500" data-id="element-42">Disclaimer:</strong> This product
            is for educational purposes only. We are not attorneys and this is
            not legal advice. Results vary based on your specific credit
            history. Individual results are not guaranteed.
          </p>

          <div className="flex items-center justify-center gap-6 mb-4" data-id="element-43">
            <button onClick={() => setActiveModal('privacy')} className="text-gray-500 hover:text-gold transition-colors text-xs uppercase tracking-wider font-medium" data-id="element-44">
              Privacy Policy
            </button>
            <span className="text-dark-200" data-id="element-45">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-gray-500 hover:text-gold transition-colors text-xs uppercase tracking-wider font-medium" data-id="element-46">
              Terms & Conditions
            </button>
          </div>

          <p className="text-gray-700 text-xs" data-id="element-47">
            &copy; {new Date().getFullYear()} The Credit Repair Sledgehammer.
            All rights reserved.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-48">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-49" />}
      </AnimatePresence>
    </>;
}